import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../../components/AuthLayout";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { ErrorMessage } from "../../components/ErrorMessage";
import { Field } from "../../components/Field";
import { Loading } from "../../components/Loading";

function friendly(message) {
  return String(message || "Something went wrong.").replace(/^\d{3}\s+/, "");
}

const MIN_PASSWORD = 8;

// Public page reached from an archer-invitation email. A `claim` invitation
// promotes a managed archer into its own login account.
export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { isAuthenticated, acceptAuth } = useAuth();

  // status: "loading" | "ready" | "invalid"
  const [status, setStatus] = useState(token ? "loading" : "invalid");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .invitationInfo(token)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function accept(payload) {
    setError(null);
    setBusy(true);
    try {
      const data = await api.acceptInvite(token, payload);
      acceptAuth(data);
      navigate("/");
    } catch (err) {
      setError(friendly(err.message));
      setBusy(false);
    }
  }

  async function onCreateAccount(e) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    await accept({ password, name });
  }

  if (status === "loading") {
    return (
      <AuthLayout title="Invitation">
        <Loading message="Loading your invitation…" />
      </AuthLayout>
    );
  }

  if (status === "invalid") {
    return (
      <AuthLayout
        title="Invitation"
        footer={
          <span>
            <Link to="/login">Back to sign in</Link>
          </span>
        }
      >
        <ErrorMessage message="This invitation link is invalid or has expired." />
      </AuthLayout>
    );
  }

  const isClaim = info.kind === "claim";
  const title = isClaim
    ? `Claim ${info.archer_name}`
    : `Help manage ${info.archer_name}`;

  // No account exists yet for the invited email — create one.
  if (info.needs_account) {
    return (
      <AuthLayout title={title}>
        <p className="auth-msg">
          {isClaim ? (
            <>
              Set a password to claim <b>{info.archer_name}</b> as the account
              for <b>{info.email}</b>.
            </>
          ) : (
            <>
              Create an X·RING account for <b>{info.email}</b> to help manage{" "}
              <b>{info.archer_name}</b>.
            </>
          )}
        </p>
        <form className="auth-form" onSubmit={onCreateAccount}>
          <ErrorMessage message={error} />
          {/* A co-manager gets their own archer too, so they name themselves. */}
          {!isClaim && (
            <Field variant="auth" label="Your name">
              <input
                className="input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          )}
          <Field variant="auth" label="Password">
            <input
              type="password"
              className="input"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="auth-hint">At least {MIN_PASSWORD} characters.</span>
          </Field>
          <button type="submit" className="btn primary auth-submit" disabled={busy}>
            {busy ? "Setting up…" : isClaim ? "Claim account" : "Create account"}
          </button>
        </form>
      </AuthLayout>
    );
  }

  // An account already exists for the invited email — it must be signed in.
  if (!isAuthenticated) {
    return (
      <AuthLayout
        title={title}
        footer={
          <span>
            <Link to="/login">Sign in</Link>
          </span>
        }
      >
        <div className="auth-msg" role="status">
          An X·RING account already exists for <b>{info.email}</b>. Sign in to
          that account, then reopen this invitation link to accept it.
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={title}>
      <ErrorMessage message={error} />
      <p className="auth-msg">
        Accept this invitation to add <b>{info.archer_name}</b> to your account.
      </p>
      <button
        className="btn primary auth-submit"
        disabled={busy}
        onClick={() => accept({})}
      >
        {busy ? "Accepting…" : "Accept invitation"}
      </button>
    </AuthLayout>
  );
}
